import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import {
	BehaviorSubject,
	Observable,
	Subject,
	delay,
	distinctUntilChanged,
	filter,
	fromEvent,
	interval,
	map,
	merge,
	of,
	switchMap,
	takeUntil,
	tap,
	zipWith,
} from 'rxjs';


import { ContentComponentForPopUpComponent } from 'ib/internetbank-core/ib-components/pop-ups/pop-up/pop-up.component';
import { ScreenModeService } from 'ib/internetbank-core/ib-services/screen-mode.service';

import { LogData, UseractivityDataService } from '../../../common/services/useractivity.data.service';
import { ProductSuggestionItem } from '../../../products/left-panel/product-list/product-suggestion-list/models/product-suggestion.entities';
import { ProductSuggestionListFacade } from '../../../products/left-panel/product-list/product-suggestion-list/product-suggestion-list.facade';
import { SuggestionLinkUtil } from '../../../sections/suggestions-section/utils/suggestion-link.utils';
import { RouteAction } from '../../notifications/models/route-action';
import { StoriesList } from '../models/stories-list';
import {
	AnimationState,
	SlideDirection,
	Story,
	StorySlideIndexed,
} from '../models/stories.entities';
import { StoriesFacadeService } from '../stories.facade.service';

@Component({
	selector: 'app-story-pop-up',
	templateUrl: './story-pop-up.component.html',
	styleUrls: ['./story-pop-up.component.less'],
	standalone: false,
})
export class StoryPopUpComponent extends ContentComponentForPopUpComponent implements OnInit, AfterViewInit, OnDestroy {


	@Input()
	public storyIndex: number = 0;

	@Input()
	public stories: Array<Story>;


	@ViewChild('storyContent')
	private storyElement: ElementRef;

	public readonly popUpId: string = StoryPopUpComponent.name;

	public storiesList: StoriesList | undefined;

	private showStoryLoader$$: Subject<boolean> = new Subject();
	// eslint-disable-next-line @typescript-eslint/member-ordering
	public showStoryLoader$: Observable<boolean> = this.showStoryLoader$$.asObservable();

	private animationState$$: Subject<AnimationState> = new BehaviorSubject('running');
	// eslint-disable-next-line @typescript-eslint/member-ordering
	public animationState$: Observable<AnimationState> = this.animationState$$.asObservable();

	private readonly SWIPE_DELTA: number = 60;


	constructor(
		cd: ChangeDetectorRef,
		private storiesFacadeService: StoriesFacadeService,
		private router: Router,
		private productSuggestionListFacade: ProductSuggestionListFacade,
		private useractivityDataService: UseractivityDataService,
	) {
		super(cd);
	}


	public ngOnInit(): void {
		this.storiesList = new StoriesList(this.stories);
		this.storiesList.slideChange$
			.pipe(
				distinctUntilChanged((previous: StorySlideIndexed, current: StorySlideIndexed) => {
					return previous.slideId === current.slideId;
				}),
				tap((storySlideIndexed: StorySlideIndexed) => {
					this.useractivityDataService.log(
						new LogData(
							'STORY',
							'STORY_SLIDER',
							'SHOW',
							[
								{
									param: 'slide_Id',
									value: `${storySlideIndexed.story.storyId}_${storySlideIndexed.slideId}`,
								},
							],
						),
					)
						.subscribe();
				}),
			)
			.subscribe();
		this.openSlides(this.storyIndex);
	}

	public ngAfterViewInit(): void {
		const element = this.storyElement.nativeElement;
		const touchStart$ = fromEvent<TouchEvent>(element, 'touchstart');
		const touchMove$ = fromEvent<TouchEvent>(element, 'touchmove');
		const touchEnd$ = fromEvent<TouchEvent>(element, 'touchend');

		// longtap - pause story
		touchStart$
			.pipe(
				tap(() => {
					this.storiesList?.storyItem.pause();
					this.animationState$$.next('paused');
				}),
				switchMap(() => interval(10).pipe(
					takeUntil(merge(touchMove$, touchEnd$).pipe(tap(() => {
						this.storiesList?.storyItem.resume();
						this.animationState$$.next('running');
					}))))),
			)
			.subscribe();

		// swipes - change/close story
		touchStart$
			.pipe(zipWith(touchEnd$))
			.subscribe(([
				touchStartEvent,
				touchEndEvent]: [TouchEvent, TouchEvent,
				]) => {
				this.handleTouch(touchStartEvent, touchEndEvent);
			});
	}

	public override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.storiesList?.stop(true);
	}

	public isShowButton(curSlide: StorySlideIndexed): boolean {
		return !!curSlide.buttonLink || !!(curSlide.buttonName && curSlide.slideType === 'NBA');
	}

	public handleTouch(touchStartEvent: TouchEvent, touchEndEvent: TouchEvent): void {
		const touchStart = touchStartEvent.touches[0] || touchStartEvent.changedTouches[0];
		const touchEnd = touchEndEvent.touches[0] || touchEndEvent.changedTouches[0];

		const deltaX = touchEnd.pageX - touchStart.pageX;
		const deltaY = touchEnd.pageY - touchStart.pageY;
		const deltaTime = touchEndEvent.timeStamp - touchStartEvent.timeStamp;

		// simulte a swipe -> less than 500 ms and more than SWIPE_DELTA
		if (deltaTime < 500) {
			if (Math.abs(deltaX) > this.SWIPE_DELTA) {
				if (deltaX > 0) {
					this.doSwipeRight();
				} else {
					this.doSwipeLeft();
				}
			}

			if (Math.abs(deltaY) > this.SWIPE_DELTA) {
				if (deltaY > 0) {
					this.doSwipeDown();
				}
			}
		}
	}


	public openSlides(storyIndex: number): void {
		this.storiesList?.play(storyIndex)
			.onFinished(() => this.close());

		this.storiesList?.storyChange$.pipe(
			takeUntil(this.unsubscribe$),
			tap(() => this.showStoryLoader$$.next(true)),
			delay(500),
			tap(() => this.showStoryLoader$$.next(false)),
		).subscribe((story: Story) => {

			if (!story.isViewed) {
				this.storiesFacadeService.markAsViewed(story.storyId);
			}

		});
	}

	public handleSlideButtonClick(slide: StorySlideIndexed): void {

		this.useractivityDataService.log(
			new LogData(
				'STORY',
				'STORY_SLIDER',
				'CLICK',
				[
					{
						param: 'slide_Id',
						value: `${slide.story.storyId}_${slide.slideId}`,
					},
				],
			),
		)
			.subscribe();

		this.close();

		// eslint-disable-next-line no-useless-assignment
		let action$: Observable<RouteAction | undefined> = of(undefined);

		switch (true) {
			case !!slide.buttonLink:
				{
					if (slide.buttonLink.indexOf('http') === -1) {
						const route = ScreenModeService.getRouteLinkByScreenMode(slide.buttonLink);
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						this.router.navigateByUrl(route[0] as any);
					} else {
						window.location.href = slide.buttonLink;
					}

					action$ = of(undefined);
				}
				break;

			case slide.buttonName && slide.slideType === 'NBA':
				{
					action$ =
						this.productSuggestionListFacade.getSuggestionAllList()
							.pipe(
								switchMap(() => {
									return this.productSuggestionListFacade.suggestionAllList$
										.pipe(
											filter((productSuggestions: Array<ProductSuggestionItem>) => !!productSuggestions.length),
											map((productSuggestions: Array<ProductSuggestionItem>) => {
												const element: ProductSuggestionItem | undefined = productSuggestions.find((productSuggestionItem: ProductSuggestionItem) => productSuggestionItem.id === slide.suggestionId);

												const suggestionRouteAction = element
													? SuggestionLinkUtil.getSuggestionRouteAction(
														element,
													)
													: undefined;

												return suggestionRouteAction;
											}),
											tap((routeAction: RouteAction | undefined) => {
												if (routeAction?.onClickHandler) {
													routeAction.onClickHandler(null);
												} else if (routeAction?.route) {
													this.router.navigate(routeAction.route, { queryParams: routeAction.queryParams });
												}
											}),
											map(() => {
												return undefined;
											}),
										);
								}),
							);
				}
				break;

			default:
				action$ = of(undefined);
				break;
		}

		action$.subscribe();

	}

	public close(): void {
		this.closePopup?.();
		this.storiesList?.stop();
	}

	public handleSlideClick(e: MouseEvent): void {
		e.stopPropagation();

		if (e.button !== 0) {	// 0 - left button
			return;
		}

		const clickTarget = e.target as HTMLElement;
		const clickTargetWidth = clickTarget?.offsetWidth;
		const xCoordInClickTarget = e.clientX - clickTarget.getBoundingClientRect().left;
		this.changeSlide(clickTargetWidth / 2 > xCoordInClickTarget ? -1 : 1);
	}

	private changeSlide(direction: SlideDirection): void {
		if (direction === 1) {
			this.storiesList?.next();
		} else {
			this.storiesList?.prev();
		}
	}

	private doSwipeDown(): void {
		this.close();
	}

	private doSwipeRight(): void {
		this.storiesList?.playPrevStory();
	}

	private doSwipeLeft(): void {
		this.storiesList?.playNextStory();
	}

}
